import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { peopleService } from 'src/data/api/orbit-service/peopleService';
import { PeopleSuggestion } from 'src/types/orbit/People';

/** Minimum characters before the alias-autocomplete backend is queried. */
export const MIN_ALIAS_CHARS = 2;
/** Debounce applied to keystrokes before a request is issued. */
export const DEBOUNCE_MS = 300;
/** Max suggestions requested per query. */
export const SUGGEST_LIMIT = 10;
/** How long a query result stays fresh (repeat prefixes hit cache). */
const STALE_TIME_MS = 30_000;

interface UsePeopleSearchResult {
  suggestions: PeopleSuggestion[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Debounced, cached employee search backed by Orbit's SuggestPeople.
 *
 * Efficiency + correctness:
 * - Length gate: no request below {@link MIN_ALIAS_CHARS}.
 * - Debounce: only settled input (300ms) triggers a call.
 * - React Query keys on the debounced term, so stale in-flight responses are
 *   ignored (race-safe) and repeated prefixes are served from cache.
 */
export const usePeopleSearch = (
  programId: string,
  query: string,
): UsePeopleSearchResult => {
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const trimmed = query.trim();
    const handle = setTimeout(() => setDebouncedQuery(trimmed), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query]);

  const enabled = !!programId && debouncedQuery.length >= MIN_ALIAS_CHARS;

  const { data, isFetching, isError } = useQuery({
    queryKey: ['people', 'suggest', programId, debouncedQuery],
    queryFn: () =>
      peopleService.suggest({
        programId,
        query: debouncedQuery,
        limit: SUGGEST_LIMIT,
      }),
    enabled,
    staleTime: STALE_TIME_MS,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const suggestions = useMemo(() => data?.suggestions ?? [], [data]);

  return {
    suggestions,
    isLoading: enabled && isFetching,
    isError,
  };
};
